/** @format */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useContentProtection } from "../hooks/useContentProtection";
import { usePerformanceOptimization } from "../hooks/usePerformanceOptimization";
import { usePerformanceMonitoring } from "../hooks/usePerformanceMonitoring";
import { useCourses } from "../hooks/useCourses";
import { useCart } from "../contexts/CartContext";
import PublicFooter from "../components/PublicFooter";
import FeaturedCourseCard from "../components/FeaturedCourseCard";
import im1 from "../assets/im1.jpeg";
import im2 from "../assets/im2.jpeg";
import im3 from "../assets/im3.jpeg";
import {
  AcademicCapIcon,
  BookOpenIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  StarIcon,
  DocumentTextIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  DevicePhoneMobileIcon,
  ClockIcon,
  UserIcon,
  TrophyIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  PlayIcon,
  BuildingOfficeIcon,
  HandThumbUpIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";

const LandingPage: React.FC = React.memo(() => {
  const { currentUser, loading } = useAuth();
  const { featuredCourses, loading: coursesLoading, fetchFeaturedCourses } = useCourses({ autoFetch: false, useCacheOnInit: false });
  const { addToCart } = useCart();
  
  // Content protection and performance optimization
  useContentProtection(true);
  usePerformanceOptimization();
  usePerformanceMonitoring();

  // Memoize the loadFeaturedCourses function to prevent recreation
  const loadFeaturedCourses = useCallback(async () => {
    try {
      await fetchFeaturedCourses();
    } catch (error) {
      console.error('Error fetching featured courses:', error);
    }
  }, [fetchFeaturedCourses]);

  // Memoize addToCart to prevent unnecessary re-renders
  const handleAddToCart = useCallback((item: any) => {
    addToCart(item);
  }, [addToCart]);

  // Fetch featured courses only once on mount
  useEffect(() => {
    loadFeaturedCourses();
  }, []); // Empty dependency array - only run once on mount

  // Memoize features array to prevent recreation
  const features = useMemo(() => [
    {
      icon: AcademicCapIcon,
      title: "Professional Courses",
      description:
        "Access to ICAG and CITG accredited courses with expert instructors",
    },
    {
      icon: BookOpenIcon,
      title: "Digital Library",
      description:
        "Comprehensive collection of books, videos, and study materials",
    },
    {
      icon: UserGroupIcon,
      title: "Community Learning",
      description: "Join Discord communities and connect with fellow students",
    },
  ], []);

  // Redirect authenticated mobile users to dashboard
  const isMobile = useMemo(() => {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  }, []);

  if (currentUser && isMobile) {
    return <Navigate to="/app" replace />;
  }

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-blue-600 text-lg font-medium">Checking your account...</p>
        </div>
      </div>
    );
  }

  const stats = [
    { number: "1930+", label: "Students Empowered" },
    { number: "58+", label: "Subjects Covered" },
    { number: "92%", label: "Success Rate" },
    { number: "10+", label: "Years of Excellence" },
  ];

  const whyChooseFeatures = [
    {
      icon: DocumentTextIcon,
      title: "Comprehensive Course Content",
      description: "Access detailed study materials, practice questions, and expert guidance for every topic",
      features: ["Detailed study materials", "Practice questions", "Expert guidance"]
    },
    {
      icon: UserIcon,
      title: "Expert Instructors",
      description: "Learn from industry experts with years of experience in accounting and finance",
      features: ["Industry experts", "Practical experience", "Personalized support"]
    },
    {
      icon: PlayIcon,
      title: "Interactive Learning",
      description: "Engage with interactive modules and real-world case studies",
      features: ["Interactive modules", "Case studies", "Practice exercises"]
    },
    {
      icon: TrophyIcon,
      title: "Certification",
      description: "Earn recognized certifications upon course completion",
      features: ["Industry recognition", "Career advancement", "Professional growth"]
    },
    {
      icon: ClockIcon,
      title: "24/7 Support",
      description: "Get help whenever you need it with our dedicated support team",
      features: ["Round-the-clock assistance", "Quick response time", "Expert guidance"]
    },
    {
      icon: DevicePhoneMobileIcon,
      title: "Mobile Learning",
      description: "Learn on the go with our mobile-friendly platform",
      features: ["Mobile app access", "Offline learning", "Progress tracking"]
    }
  ];

  const courseCategories = [
    {
      title: "ICAGH",
      description: "Professional accounting courses designed for ICAG certification success",
      courses: "15+ Courses",
      students: "1000+ Students",
      color: "blue"
    },
    {
      title: "CITGH",
      description: "Comprehensive training for Chartered Institute of Taxation certification",
      courses: "12+ Courses",
      students: "800+ Students",
      color: "green"
    },
    {
      title: "Corporate Training",
      description: "Customized training programs for organizational development",
      courses: "10+ Programs",
      students: "50+ Companies",
      color: "purple"
    },
    {
      title: "Digital Marketing",
      description: "Master modern marketing strategies and digital tools",
      courses: "8+ Courses",
      students: "500+ Students",
      color: "pink"
    },
    {
      title: "Business Growth",
      description: "Strategies and tools for sustainable business expansion",
      courses: "6+ Courses",
      students: "300+ Students",
      color: "indigo"
    }
  ];

  const testimonials = [
    {
      name: "Martin Asare",
      role: "Accounts Manager",
      quote: "Prof has the ability to break down concepts to your levels. I never believed I could pass Corporate Reporting on my first try. The support and guidance were exceptional!",
      achievement: "ICAGH Graduate",
      avatar: "MA"
    },
    {
      name: "Ephraim Tetteh",
      role: "Senior Auditor",
      quote: "Profstudymate has transformed the way I learn for my CA exam even with my busy schedule. The courses are well structured and the instructors are incredibly knowledgeable.",
      achievement: "CITGH Certified",
      avatar: "ET"
    },
    {
      name: "Isaac Kumi",
      role: "Internal Auditor",
      quote: "Prof told me I could pass my CITG exam on first attempt but I didn't believe. Fast forward to today, I am shocked… We just graduated. The guy knows his stuff!",
      achievement: "First Attempt Success",
      avatar: "IK"
    }
  ];

  const successStories = [
    {
      name: "Kwame Mensah",
      role: "Senior Accountant",
      story: "After completing the ICAG program, I was promoted to Senior Accountant within 6 months. The practical knowledge and skills I gained were invaluable.",
      achievement: "ICAG Graduate",
      type: "Career Advancement"
    },
    {
      name: "Ama Osei",
      role: "Tax Consultant",
      story: "The CITG program helped me transition from finance to taxation. The practical approach and industry connections were game-changing for my career.",
      achievement: "CITG Graduate",
      type: "Career Switch"
    },
    {
      name: "Kofi Asante",
      role: "Entrepreneur",
      story: "The business growth program transformed my small accounting firm. We've doubled our client base and expanded our services within a year.",
      achievement: "Business Growth Graduate",
      type: "Business Growth"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-green-900 overflow-hidden flex items-center px-4 sm:px-0">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-black/40"></div>
          
          {/* Animated grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.1)_1px,transparent_1px)] bg-[size:50px_50px] animate-pulse"></div>
          
          {/* Floating geometric shapes */}
          <div className="absolute top-10 sm:top-20 left-10 sm:left-20 w-20 h-20 sm:w-32 sm:h-32 bg-blue-500/20 rounded-full blur-lg sm:blur-xl animate-float"></div>
          <div className="absolute top-20 sm:top-40 right-16 sm:right-32 w-16 h-16 sm:w-24 sm:h-24 bg-green-500/20 rounded-full blur-lg sm:blur-xl animate-float-delayed"></div>
          <div className="absolute bottom-16 sm:bottom-32 left-1/4 sm:left-1/3 w-16 h-16 sm:w-20 sm:h-20 bg-purple-500/20 rounded-full blur-lg sm:blur-xl animate-float-slow"></div>
          <div className="absolute top-1/2 right-16 sm:right-1/4 w-12 h-12 sm:w-16 sm:h-16 bg-yellow-500/20 rounded-full blur-lg sm:blur-xl animate-bounce"></div>
        </div>

        {/* Main content */}
        <div className="relative z-10 w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-screen py-12 sm:py-16 lg:py-20">
              
              {/* Left side - Text content */}
              <div className="text-center lg:text-left space-y-6 sm:space-y-8">
                {/* Badge */}
                <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500/20 to-green-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-blue-200 text-xs sm:text-sm font-medium animate-fade-in-up">
                  <StarIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-2 fill-current text-yellow-400" />
                  <span className="hidden sm:inline">#1 Professional Learning Platform</span>
                  <span className="sm:hidden">#1 Platform</span>
                </div>

                {/* Main headline */}
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight animate-fade-in-up animation-delay-200">
                  <span className="bg-gradient-to-r from-white via-blue-100 to-green-100 bg-clip-text text-transparent">
                    {currentUser ? "Welcome Back!" : "Your Gateway to"}
                  </span>
                  <br />
                  <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-green-400 bg-clip-text text-transparent">
                    {currentUser ? "Continue Learning" : "Professional Excellence"}
                  </span>
                </h1>

                {/* Subtitle */}
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 leading-relaxed max-w-2xl mx-auto lg:mx-0 animate-fade-in-up animation-delay-400">
                  {currentUser 
                    ? `Welcome back, ${currentUser.name || 'Student'}! Continue your learning journey with our expert-led courses and comprehensive study materials.`
                    : "Join thousands of students preparing for ICAG and CITG professional qualifications. Access expert-led courses, comprehensive study materials, and a supportive learning community."
                  }
                </p>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-3 sm:gap-6 py-4 sm:py-6 animate-fade-in-up animation-delay-600">
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1">1930+</div>
                    <div className="text-xs sm:text-sm text-gray-400">Students</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1">92%</div>
                    <div className="text-xs sm:text-sm text-gray-400">Success Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1">10+</div>
                    <div className="text-xs sm:text-sm text-gray-400">Years</div>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 animate-fade-in-up animation-delay-800 max-w-sm sm:max-w-none mx-auto lg:mx-0">
                  <Link
                    to={currentUser ? "/app/catalog" : "/signup"}
                    className="group inline-flex items-center justify-center px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white text-sm sm:text-base lg:text-lg font-semibold rounded-lg sm:rounded-xl lg:rounded-2xl transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-blue-500/25 border border-transparent hover:border-white/20"
                  >
                    <span className="hidden sm:inline">{currentUser ? "Browse Courses" : "Start Learning Today"}</span>
                    <span className="sm:hidden">{currentUser ? "Browse Courses" : "Start Learning"}</span>
                    <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  
                  <Link 
                    to={currentUser ? "/app" : "/signin"} 
                    className="group inline-flex items-center justify-center px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm sm:text-base lg:text-lg font-semibold rounded-lg sm:rounded-xl lg:rounded-2xl hover:bg-white/20 hover:border-white/40 transform hover:scale-105 transition-all duration-300"
                  >
                    <span className="hidden sm:inline">{currentUser ? "Go to Dashboard" : "I Already Have an Account"}</span>
                    <span className="sm:hidden">{currentUser ? "Dashboard" : "Sign In"}</span>
                    <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>

                {/* Trust indicators */}
                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-3 sm:space-y-0 sm:space-x-6 pt-4 animate-fade-in-up animation-delay-1000">
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                    <span className="text-gray-300 text-xs sm:text-sm">ICAG Accredited</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                    <span className="text-gray-300 text-xs sm:text-sm">CITG Certified</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                    <span className="text-gray-300 text-xs sm:text-sm">24/7 Support</span>
                  </div>
                </div>
              </div>

              {/* Right side - Visual content */}
              <div className="relative block animate-fade-in-up animation-delay-400 order-first lg:order-last">
                {/* Main hero image */}
                <div className="relative">
                  <div className="w-full h-64 sm:h-80 lg:h-96 bg-gradient-to-br from-blue-500/20 to-green-500/20 rounded-2xl lg:rounded-3xl border border-white/20 backdrop-blur-sm overflow-hidden">
                    <img 
                      src={im1} 
                      alt="Professional Learning" 
                      className="w-full h-full object-cover mix-blend-overlay"
                    />
                  </div>
                  
                  {/* Floating cards - Hidden on very small screens, visible on sm+ */}
                  <div className="absolute -top-4 sm:-top-8 -right-4 sm:-right-8 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-2xl animate-float hidden sm:block">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-green-500 rounded-lg sm:rounded-xl flex items-center justify-center">
                        <AcademicCapIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                      <div>
                        <div className="text-white font-semibold text-sm sm:text-base">Expert Courses</div>
                        <div className="text-gray-300 text-xs sm:text-sm">ICAG & CITG</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute -bottom-4 sm:-bottom-8 -left-4 sm:-left-8 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-2xl animate-float-delayed hidden sm:block">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg sm:rounded-xl flex items-center justify-center">
                        <BookOpenIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                      <div>
                        <div className="text-white font-semibold text-sm sm:text-base">Study Materials</div>
                        <div className="text-gray-300 text-xs sm:text-sm">Digital Library</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-2 sm:h-3 bg-white/60 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Featured Courses Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-green-50 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-green-50/50 opacity-50"></div>
        
        {/* Featured badge */}
        <div className="absolute top-8 right-8">
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-3 rounded-full shadow-lg transform rotate-3 animate-pulse">
            <div className="flex items-center space-x-2">
              <StarIcon className="h-5 w-5 fill-current" />
              <span className="font-bold text-sm">FEATURED</span>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-green-100 rounded-full mb-6">
              <BookOpenIcon className="h-5 w-5 text-blue-600 mr-2" />
              <span className="text-blue-700 font-semibold text-sm">Premium Selection</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent">
              Featured Courses
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover our handpicked selection of top-rated courses designed to accelerate your learning journey. 
              These featured courses represent the best of what we offer.
            </p>
          </div>

          {/* Featured Courses Grid */}
          {coursesLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
                <div className="absolute inset-0 rounded-full border-4 border-blue-200 opacity-30"></div>
              </div>
            </div>
          ) : featuredCourses.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {featuredCourses.map((course, index) => (
                <FeaturedCourseCard
                  key={course.id}
                  course={course}
                  index={index}
                  currentUser={currentUser}
                  addToCart={handleAddToCart}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpenIcon className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-lg mb-2">No featured courses available at the moment</p>
              <p className="text-gray-400 text-sm">Check back later for our latest featured courses</p>
            </div>
          )}

          <div className="text-center">
            <Link 
              to={currentUser ? "/app/catalog" : "/catalog"} 
              className="btn-primary text-lg px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center"
            >
              View All Courses
              <ArrowRightIcon className="ml-2 h-5 w-5 animate-bounce-x" />
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Access Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-green-50 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-green-100/20"></div>
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Enhanced Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-100 to-green-100 rounded-full mb-6 border border-blue-200">
              <AcademicCapIcon className="h-6 w-6 text-blue-600 mr-2" />
              <span className="text-blue-700 font-semibold text-sm">Learning Hub</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent">
              {currentUser ? "Continue Your Learning Journey" : "Explore Our Platform"}
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
              {currentUser 
                ? "Pick up where you left off and accelerate your path to professional success. Your learning journey continues here with personalized progress tracking and expert guidance."
                : "Discover what ProfStudy has to offer - no account required to browse our comprehensive learning resources and expert-led courses."
              }
            </p>

            {/* Progress indicator for logged-in users */}
            {currentUser && (
              <div className="inline-flex items-center px-6 py-3 bg-white rounded-2xl shadow-lg border border-gray-100 mb-8">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-gray-700">Active Learner</span>
                  </div>
                  <div className="w-px h-6 bg-gray-300"></div>
                  <div className="flex items-center space-x-2">
                    <TrophyIcon className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm font-medium text-gray-700">Keep Going!</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Learning Cards Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Clean Course Card */}
            <Link
              to={currentUser ? "/app/catalog" : "/catalog"}
              className="group bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 hover:border-blue-200 relative overflow-hidden"
            >
              {/* Subtle background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-green-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Icon */}
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <AcademicCapIcon className="h-8 w-8 text-white" />
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors relative z-10">
                {currentUser ? "My Courses" : "Browse Courses"}
              </h3>
              
              <p className="text-gray-600 mb-6 leading-relaxed relative z-10">
                {currentUser 
                  ? "Continue your enrolled courses and track your progress with expert guidance."
                  : "Explore our comprehensive course catalog with expert-led content designed for ICAG and CITG success."
                }
              </p>

              {/* Simple CTA */}
              <div className="inline-flex items-center text-blue-600 font-semibold group-hover:text-blue-700 transition-colors relative z-10">
                <span className="mr-2">{currentUser ? "Continue Learning" : "Start Exploring"}</span>
                <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>

              {/* Progress bar for logged-in users */}
              {currentUser && (
                <div className="mt-6 relative z-10">
                  <div className="flex justify-between text-sm text-gray-500 mb-2">
                    <span>Progress</span>
                    <span>75%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500" style={{width: '75%'}}></div>
                  </div>
                </div>
              )}
            </Link>

            {/* Clean Materials Card */}
            <Link
              to={currentUser ? "/app/shop" : "/shop"}
              className="group bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 hover:border-green-200 relative overflow-hidden"
            >
              {/* Subtle background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Icon */}
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <BookOpenIcon className="h-8 w-8 text-white" />
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors relative z-10">
                {currentUser ? "My Materials" : "Study Materials"}
              </h3>
              
              <p className="text-gray-600 mb-6 leading-relaxed relative z-10">
                {currentUser 
                  ? "Access your purchased books, study resources, and digital library."
                  : "Access comprehensive books, videos, and resources to enhance your learning."
                }
              </p>

              {/* Simple CTA */}
              <div className="inline-flex items-center text-green-600 font-semibold group-hover:text-green-700 transition-colors relative z-10">
                <span className="mr-2">{currentUser ? "View Materials" : "Browse Materials"}</span>
                <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>

              {/* Materials count for logged-in users */}
              {currentUser && (
                <div className="mt-6 relative z-10">
                  <div className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    <BookOpenIcon className="h-4 w-4 mr-1" />
                    12 Materials Available
                  </div>
                </div>
              )}
            </Link>

            {/* Clean Affiliate Card */}
            <Link
              to={currentUser ? "/affiliate" : "/affiliate"}
              className="group bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 hover:border-red-200 relative overflow-hidden"
            >
              {/* Subtle background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Icon */}
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <UserGroupIcon className="h-8 w-8 text-white" />
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors relative z-10">
                {currentUser ? "My Affiliate" : "Refer & Earn"}
              </h3>
              
              <p className="text-gray-600 mb-6 leading-relaxed relative z-10">
                {currentUser 
                  ? "Track your referrals and earnings while helping others succeed."
                  : "Join our affiliate program and earn money by referring friends."
                }
              </p>

              {/* Simple CTA */}
              <div className="inline-flex items-center text-red-600 font-semibold group-hover:text-red-700 transition-colors relative z-10">
                <span className="mr-2">{currentUser ? "View Earnings" : "Learn More"}</span>
                <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>

              {/* Earnings preview for logged-in users */}
              {currentUser && (
                <div className="mt-6 relative z-10">
                  <div className="inline-flex items-center px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                    <TrophyIcon className="h-4 w-4 mr-1" />
                    ₵150 Earned
                  </div>
                </div>
              )}
            </Link>
          </div>

          {/* Motivational Call-to-Action */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-3xl p-8 text-white relative overflow-hidden">
              {/* Background pattern */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 opacity-30"></div>
              
              <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-4">Ready to Accelerate Your Success?</h3>
                <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                  {currentUser 
                    ? "Your learning journey is already in progress. Keep the momentum going and achieve your professional goals faster than ever."
                    : "Join thousands of successful professionals who have transformed their careers with ProfStudy. Start your journey today."
                  }
                </p>
                
                <Link
                  to={currentUser ? "/app/catalog" : "/signup"}
                  className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-2xl hover:bg-blue-50 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {currentUser ? "Continue Learning" : "Start Your Journey"}
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up">
              Why Choose ProfStudy?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
              We provide everything you need to succeed in your professional
              journey
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6 group hover:bg-white hover:shadow-xl rounded-2xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="h-8 w-8 text-blue-600 group-hover:text-green-600 transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 group-hover:text-gray-700 transition-colors">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up">
              Professional Programs
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
              Choose your path to professional success
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg border border-blue-200 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-blue-700 font-bold text-lg">IC</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">ICAG</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Institute of Chartered Accountants Ghana - Master the
                fundamentals of accounting, auditing, and financial management
                with our comprehensive course offerings.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                  Professional accounting courses
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                  Expert-led instruction
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                  Study materials & resources
                </li>
              </ul>
              <Link to="/signup" className="btn-primary w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 transition-all duration-300">
                Enroll in ICAG
              </Link>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg border border-green-200 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-green-700 font-bold text-lg">CT</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">CITG</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Chartered Institute of Taxation Ghana - Develop expertise in tax
                law, compliance, and tax planning through our specialized
                curriculum.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                  Tax law & compliance courses
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                  Practical tax applications
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                  Industry expert guidance
                </li>
              </ul>
              <Link to="/signup" className="btn-primary w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 transform hover:scale-105 transition-all duration-300">
                Enroll in CITG
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-blue-700 to-green-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-green-600/90"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="group hover:scale-110 transition-transform duration-300">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2 group-hover:text-yellow-200 transition-colors duration-300">
                  {stat.number}
                </div>
                <div className="text-blue-100 text-sm md:text-base group-hover:text-white transition-colors duration-300">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Animated background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 bg-white/10 rounded-full animate-pulse animation-delay-1000"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-white/10 rounded-full animate-pulse animation-delay-2000"></div>
        </div>
      </section>

      {/* Why Choose Our Platform Section */}
      <section className="py-20 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up">
              Why Choose Our Platform?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
              Experience the difference with our comprehensive learning platform designed for your success
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 hover:scale-105 group">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="h-6 w-6 text-blue-600 group-hover:text-green-600 transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mb-4 group-hover:text-gray-700 transition-colors">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.features.map((item, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600 group-hover:text-gray-700 transition-colors">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 group-hover:scale-110 transition-transform" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose ProfStudyMate Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up">
              Why Choose ProfStudyMate?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
              ProfStudyMate is dedicated to providing top-notch accounting education. Our platform is designed to be interactive and engaging, ensuring that you get the best learning experience possible.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center mb-12">
            <div className="group hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-blue-600 mb-2 group-hover:text-blue-700 transition-colors">95%</div>
              <div className="text-gray-600 group-hover:text-gray-700 transition-colors">First Attempt Pass Rate</div>
            </div>
            <div className="group hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-green-600 mb-2 group-hover:text-green-700 transition-colors">2000+</div>
              <div className="text-gray-600 group-hover:text-gray-700 transition-colors">Students Helped</div>
            </div>
            <div className="group hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-red-600 mb-2 group-hover:text-red-700 transition-colors">50+</div>
              <div className="text-gray-600 group-hover:text-gray-700 transition-colors">Expert Instructors</div>
            </div>
            <div className="group hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-blue-600 mb-2 group-hover:text-blue-700 transition-colors">24/7</div>
              <div className="text-gray-600 group-hover:text-gray-700 transition-colors">Support Available</div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 group hover:bg-white hover:shadow-xl rounded-2xl transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <DocumentTextIcon className="h-8 w-8 text-blue-600 group-hover:text-blue-700 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                Comprehensive Curriculum
              </h3>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors">
                Our courses cover all aspects of CITG & ICAG exams, ensuring you're fully prepared for success.
              </p>
            </div>
            <div className="text-center p-6 group hover:bg-white hover:shadow-xl rounded-2xl transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <PlayIcon className="h-8 w-8 text-green-600 group-hover:text-green-700 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                Interactive Learning
              </h3>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors">
                Engage with interactive modules, practice questions, and real-world case studies.
              </p>
            </div>
            <div className="text-center p-6 group hover:bg-white hover:shadow-xl rounded-2xl transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <ChatBubbleLeftRightIcon className="h-8 w-8 text-red-600 group-hover:text-red-700 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                Dedicated Support
              </h3>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors">
                Get help whenever you need it with our 24/7 support team and expert instructors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Categories Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up">
              Explore Our Course Categories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
              Discover specialized courses designed to help you excel in your professional accounting journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {courseCategories.map((category, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 hover:scale-105 group">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{category.title}</h3>
                  <div className={`w-8 h-8 bg-gradient-to-br from-${category.color}-100 to-${category.color}-200 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <span className={`text-${category.color}-700 font-bold text-sm`}>
                      {category.title.charAt(0)}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 mb-4 group-hover:text-gray-700 transition-colors">{category.description}</p>
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span>{category.courses}</span>
                  <span>•</span>
                  <span>{category.students}</span>
                </div>
                <Link to="/signup" className="btn-outline w-full text-center border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transform hover:scale-105 transition-all duration-300">
                  Explore Courses
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/catalog" className="btn-primary text-lg px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
              View All Categories
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up">
              What Our Students Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
              Hear from our successful students who have transformed their careers through our professional courses
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 group">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-green-100 rounded-full flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-blue-600 font-semibold group-hover:text-green-600 transition-colors">{testimonial.avatar}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4 italic group-hover:text-gray-800 transition-colors">"{testimonial.quote}"</p>
                <div className="inline-block bg-gradient-to-r from-blue-100 to-green-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium group-hover:from-blue-200 group-hover:to-green-200 transition-all duration-300">
                  {testimonial.achievement}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/testimonials" className="btn-outline text-lg px-8 py-4 border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white transform hover:scale-105 transition-all duration-300">
              View More Success Stories
            </Link>
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
              Discover how our students have transformed their careers through our professional courses
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 group">
                <div className="mb-4">
                  <span className="inline-block bg-gradient-to-r from-red-100 to-red-200 text-red-700 px-3 py-1 rounded-full text-sm font-medium mb-3 group-hover:from-red-200 group-hover:to-red-300 transition-all duration-300">
                    {story.type}
                  </span>
                  <h4 className="font-semibold text-gray-900 text-lg group-hover:text-red-600 transition-colors">{story.name}</h4>
                  <p className="text-gray-600 group-hover:text-gray-700 transition-colors">{story.role}</p>
                </div>
                <p className="text-gray-700 mb-4 group-hover:text-gray-800 transition-colors">"{story.story}"</p>
                <div className="text-sm text-blue-600 font-medium group-hover:text-blue-700 transition-colors">
                  {story.achievement}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile App & Social Section */}
      <section className="py-24 bg-gradient-to-br from-blue-50 via-white to-green-50 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200 rounded-full opacity-10 animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 bg-green-200 rounded-full opacity-10 animate-pulse animation-delay-1000"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-red-200 rounded-full opacity-10 animate-pulse animation-delay-2000"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-green-100 rounded-2xl mb-6">
              <DevicePhoneMobileIcon className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6 animate-fade-in-up">
              Stay Connected With Us
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up animation-delay-200 leading-relaxed">
              Download our mobile app and follow us on social media to stay updated with the latest courses and learning resources
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - App Downloads & Social Media */}
            <div className="space-y-8">
              {/* App Download Section */}
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-green-500 rounded-2xl flex items-center justify-center mr-4">
                    <DevicePhoneMobileIcon className="w-6 h-6 text-white" />
                  </div>
            <div>
                    <h3 className="text-2xl font-bold text-gray-900">Get Our Study Apps</h3>
                    <p className="text-gray-600">Access your courses anytime, anywhere</p>
              </div>
                </div>
                
                <div className="flex flex-row gap-4 mb-6 justify-center lg:justify-start">
                  <a
                    href="https://play.google.com/store/apps/details?id=profstudymate.appev"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get it on Google Play"
                    className="inline-flex items-center justify-center transform hover:scale-105 transition-transform duration-300"
                  >
                    <img
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt="Get it on Google Play"
                      className="h-16 w-32"
                      loading="lazy"
                    />
                  </a>
                  <a
                    href="https://apps.apple.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download on the App Store"
                    className="inline-flex items-center justify-center transform hover:scale-105 transition-transform duration-300"
                  >
                    <img
                      src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-US?size=150x50"
                      alt="Download on the App Store"
                      className="h-16 w-32"
                      loading="lazy"
                    />
                  </a>
                  <a
                    href="https://www.microsoft.com/en-us/store/apps"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get it from Microsoft"
                    className="inline-flex items-center justify-center transform hover:scale-105 transition-transform duration-300"
                  >
                    <img
                      src="https://get.microsoft.com/images/en-us%20dark.svg"
                      alt="Get it from Microsoft"
                      className="h-16 w-32"
                      loading="lazy"
                    />
                  </a>
                </div>
                
                <div className="text-center lg:text-left">
                  <p className="text-sm text-gray-500 mb-4">Available on all major platforms</p>
                  <div className="flex items-center justify-center lg:justify-start space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-green-600 font-medium">Free to download</span>
                  </div>
                </div>
              </div>

              {/* Social Media Section */}
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mr-4">
                    <GlobeAltIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Follow Our Socials</h3>
                    <p className="text-gray-600">Stay updated with latest content</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-6">
                  <a href="https://facebook.com/ProfsTraining" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="group flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl flex items-center justify-center hover:from-blue-700 hover:to-blue-800 transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true"><path d="M22 12a10 10 0 1 0-11.5 9.95v-7.04h-2.7V12h2.7V9.8c0-2.66 1.58-4.13 4-4.13 1.16 0 2.38.21 2.38.21v2.62h-1.34c-1.32 0-1.73.82-1.73 1.66V12h2.95l-.47 2.91h-2.48v7.04A10 10 0 0 0 22 12z"/></svg>
                    </div>
                    <p className="text-xs text-gray-600 text-center mt-2 group-hover:text-blue-600 transition-colors">Facebook</p>
                  </a>
                  
                  <a href="https://linkedin.com/in/profs-training-solutions/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="group flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl flex items-center justify-center hover:from-blue-600 hover:to-blue-700 transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554V14.89c0-1.326-.027-3.031-1.847-3.031-1.849 0-2.131 1.445-2.131 2.939v5.654H9.36V9h3.414v1.561h.049c.476-.9 1.637-1.847 3.37-1.847 3.6 0 4.263 2.37 4.263 5.455v6.283zM5.337 7.433a2.067 2.067 0 1 1 0-4.135 2.067 2.067 0 0 1 0 4.135zM6.813 20.452H3.861V9h2.952v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/></svg>
                    </div>
                    <p className="text-xs text-gray-600 text-center mt-2 group-hover:text-blue-600 transition-colors">LinkedIn</p>
                  </a>
                  
                  <a href="http://instagram.com/profs_training_solutions/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="group flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-pink-600 to-red-600 text-white rounded-2xl flex items-center justify-center hover:from-pink-700 hover:to-red-700 transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/></svg>
                    </div>
                    <p className="text-xs text-gray-600 text-center mt-2 group-hover:text-blue-600 transition-colors">Instagram</p>
                  </a>
                  
                  <a href="http://tiktok.com/@profstraining" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="group flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-black to-gray-800 text-white rounded-2xl flex items-center justify-center hover:from-gray-800 hover:to-black transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true"><path d="M20 8.5a6.5 6.5 0 0 1-5-2v7.2a5.2 5.2 0 1 1-5.2-5.2c.3 0 .6 0 .9.1v2.5c-.29-.12-.6-.2-.9-.2a2.7 2.7 0 1 0 2.7 2.7V2h2.6c.5 2 2.1 3.6 4.1 4.1V8.5z"/></svg>
                    </div>
                    <p className="text-xs text-gray-600 text-center mt-2 group-hover:text-blue-600 transition-colors">TikTok</p>
                  </a>
                  
                  <a href="http://youtube.com/@ProfsTrainingSolutions" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="group flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl flex items-center justify-center hover:from-red-700 hover:to-red-800 transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.6 3.5 12 3.5 12 3.5s-7.6 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.8.6 9.4.6 9.4.6s7.6 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.5v-7l6 3.5-6 3.5z"/></svg>
                    </div>
                    <p className="text-xs text-gray-600 text-center mt-2 group-hover:text-blue-600 transition-colors">YouTube</p>
                  </a>
                  
                  <a href="https://whatsapp.com/channel/0029VaZV6OeEAKWHHdSdiR1S" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="group flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl flex items-center justify-center hover:from-green-600 hover:to-green-700 transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true"><path d="M20.5 3.5A10 10 0 0 0 3.4 20.6L2 22l1.6-.4A10 10 0 1 0 20.5 3.5zM12 20a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm3.7-5.6c-.2-.1-1.3-.6-1.5-.6s-.3 0-.5.2c-.1.2-.6.7-.7.8-.1.1-.3.1-.5 0-.2-.1-.8-.3-1.5-1-.5-.4-1-1.1-1.1-1.3-.1-.2 0-.4.1-.5.1-.1.2-.3.3-.4.1-.1.1-.2.2-.3.1-.1.1-.2.1-.3s0-.3-.1-.4c-.1-.1-.5-1.2-.7-1.6-.2-.4-.4-.4-.6-.4h-.5c-.2 0-.4.1-.6.3-.2.2-.8.8-.8 1.9s.8 2.2.9 2.4c.1.2 1.6 2.4 3.9 3.3.5.2.8.3 1.1.4.5.2.9.2 1.2.1.4-.1 1.3-.5 1.5-1 .2-.5.2-.9.1-1 0-.2-.2-.3-.4-.4z"/></svg>
              </div>
                    <p className="text-xs text-gray-600 text-center mt-2 group-hover:text-blue-600 transition-colors">WhatsApp</p>
                  </a>
                  
                  <a href="https://t.me/Profstrainingsolutions" target="_blank" rel="noopener noreferrer" aria-label="Telegram" className="group flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl flex items-center justify-center hover:from-blue-600 hover:to-blue-700 transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true"><path d="M9.9 15.3l-.3 4.2c.4 0 .6-.2.8-.4l2-1.9 4.1 3c.8.4 1.3.2 1.5-.7l2.7-12.5c.3-1.3-.5-1.8-1.3-1.5L3 9.8c-1.4.6-1.4 1.4-.1 1.8l4.5 1.4 10.4-6.6c.5-.3.9-.1.6.2l-8.5 8.7z"/></svg>
            </div>
                    <p className="text-xs text-gray-600 text-center mt-2 group-hover:text-blue-600 transition-colors">Telegram</p>
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column - Mobile App Preview */}
            <div className="bg-gradient-to-br from-blue-100 to-green-100 rounded-3xl p-10 text-center hover:shadow-2xl transition-all duration-500 transform hover:scale-105 border border-blue-200">
              <div className="mb-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Mobile App Preview</h3>
                <p className="text-gray-600 text-lg">Experience learning on the go</p>
              </div>
              
              <div className="flex flex-col lg:flex-row gap-8 justify-center items-center">
                {/* iPhone Mockup 1 */}
                <div className="relative group">
                  <div className="w-48 h-96 bg-black rounded-[3rem] p-3 shadow-2xl transform rotate-3 group-hover:rotate-0 transition-all duration-500">
                    <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                      {/* iPhone notch */}
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-10"></div>
                      {/* Screen content */}
                      <div className="w-full h-full bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
                        <img 
                          src={im1} 
                          alt="ProfStudy App Screenshot 1" 
                          className="w-full h-full object-cover rounded-[2.5rem]"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-white px-3 py-1 rounded-full text-xs font-medium text-gray-700 shadow-lg">
                    Course Dashboard
                  </div>
                </div>

                {/* iPhone Mockup 2 */}
                <div className="relative group">
                  <div className="w-48 h-96 bg-black rounded-[3rem] p-3 shadow-2xl transform -rotate-3 group-hover:rotate-0 transition-all duration-500">
                    <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                      {/* iPhone notch */}
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-10"></div>
                      {/* Screen content */}
                      <div className="w-full h-full bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
                        <img 
                          src={im2} 
                          alt="ProfStudy App Screenshot 2" 
                          className="w-full h-full object-cover rounded-[2.5rem]"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-white px-3 py-1 rounded-full text-xs font-medium text-gray-700 shadow-lg">
                    CITG & ICAG Courses
                  </div>
                </div>

                {/* iPhone Mockup 3 */}
                <div className="relative group">
                  <div className="w-48 h-96 bg-black rounded-[3rem] p-3 shadow-2xl transform rotate-3 group-hover:rotate-0 transition-all duration-500">
                    <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                      {/* iPhone notch */}
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-10"></div>
                      {/* Screen content */}
                      <div className="w-full h-full bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
                        <img 
                          src={im3} 
                          alt="ProfStudy App Screenshot 3" 
                          className="w-full h-full object-cover rounded-[2.5rem]"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-white px-3 py-1 rounded-full text-xs font-medium text-gray-700 shadow-lg">
                    AI Chat
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up">
              Get in Touch
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
              Have questions about our courses? We're here to help you succeed in your professional journey.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center group hover:bg-white hover:shadow-xl rounded-2xl p-6 transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <MapPinIcon className="h-8 w-8 text-blue-600 group-hover:text-blue-700 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">Visit Us</h3>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors">
                Fefeti Drive, Spintex Road<br />
                Tema, Greater Accra
              </p>
            </div>
            <div className="text-center group hover:bg-white hover:shadow-xl rounded-2xl p-6 transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <PhoneIcon className="h-8 w-8 text-green-600 group-hover:text-green-700 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">Call Us</h3>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors">
                +233 243 44 39 34<br />
                +233 244 990 773<br />
                +233 246 752 130<br />
                <span className="text-sm text-gray-500">Mon-Fri: 9am-5pm</span>
              </p>
            </div>
            <div className="text-center group hover:bg-white hover:shadow-xl rounded-2xl p-6 transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <EnvelopeIcon className="h-8 w-8 text-red-600 group-hover:text-red-700 transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">Email Us</h3>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors">
              profstrainingsolutions@gmail.com<br />
               
              </p>
            </div>
          </div>

          <div className="text-center">
            <Link to="/contact" className="btn-primary text-lg px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
              Contact Us Today
            </Link>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-gradient-to-br from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up">
              Our Trusted Partners
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
              We collaborate with leading organizations to provide the best professional education experience
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="p-6 group hover:bg-white hover:shadow-xl rounded-2xl transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <span className="text-blue-700 font-bold text-lg group-hover:text-blue-800 transition-colors">ICAG</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">Institute of Chartered Accountants Ghana</h3>
            </div>
            <div className="p-6 group hover:bg-white hover:shadow-xl rounded-2xl transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <span className="text-green-700 font-bold text-lg group-hover:text-green-800 transition-colors">CITG</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">Chartered Institute of Taxation Ghana</h3>
            </div>
            <div className="p-6 group hover:bg-white hover:shadow-xl rounded-2xl transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <BuildingOfficeIcon className="h-10 w-10 text-red-600 group-hover:text-red-700 transition-colors" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">Corporate Partners</h3>
              <p className="text-gray-600 text-sm group-hover:text-gray-700 transition-colors">Leading businesses in Ghana</p>
            </div>
            <div className="p-6 group hover:bg-white hover:shadow-xl rounded-2xl transition-all duration-500 transform hover:-translate-y-2">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-green-100 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <HandThumbUpIcon className="h-10 w-10 text-blue-600 group-hover:text-green-600 transition-colors" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">Industry Leaders</h3>
              <p className="text-gray-600 text-sm group-hover:text-gray-700 transition-colors">Professional organizations and networks</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/signup?role=author" className="btn-outline text-lg px-8 py-4 border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transform hover:scale-105 transition-all duration-300">
              Become an Instructor
            </Link>
            <p className="text-sm text-gray-500 mt-3 max-w-md mx-auto">
              New instructor accounts require verification before publishing courses. 
              Our team reviews applications within 24-48 hours.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up">
            Ready to Start Your Professional Journey?
          </h2>
          <p className="text-xl text-gray-600 mb-8 animate-fade-in-up animation-delay-200">
            Join thousands of students who have already taken the first step
            towards their professional success with ProfStudy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-400">
            <Link to="/signup" className="btn-primary text-lg px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
              Create Free Account
            </Link>
            <Link to="/signin" className="btn-outline text-lg px-8 py-4 border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transform hover:scale-105 transition-all duration-300">
              Sign In
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
});

export default LandingPage;
